
<template>
    <flex-element
        :element-type="elementType"
        class-default-inline="f-justify-center f-flex f-flex-auto mob:f-flex-col f-flex-row f-flex-wrap f-w-1/1"
        class="f-flex f-w-1/1 fc-keywords"
        exclude-classes
        tag="div"
    >
        <placeholder v-if="searchKeywords.length === 0" text="No search keywords found." />

        <div v-else v-bind="element(1)" @element-updated="elementUpdated">
            <ul v-if="searchKeywords.length" class="fc-keywords__list">
                <li
                    v-for="(keyword, i) in searchKeywords"
                    :key="keyword.id"
                    class="fc-keywords__item"
                >
                    <button
                        type="button"
                        :class="[containerClasses, linkClasses]"
                        @click="() => onKeywordRemove(keyword, i)"
                    >
                        {{ keyword.displayName }}: {{ keyword.value }}

                        <font-awesome-icon class="fc-keywords__icon" :icon="['fal', 'times']" />
                    </button>
                </li>
            </ul>
        </div>
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import Placeholder from '@/components-user/placeholder';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { getClassDefinition } from '@/utils/tailwind-helper';
import { FIRE_FLEX_EVENT } from '@/store/types/mutation-types';
import { formatDate } from '@/utils/date';

const CUSTOM_ATTR_NAME = 'search-filter-form-id';

export default {
    components: { Placeholder },
    mixins: [baseOrganism, listenOnRootMixin],

    props: {
        displayName: {
            type: String,
            default: 'Keyword Group',
        },

        name: {
            type: String,
            default: '',
        },

        activeIndex: {
            type: Number,
            default: -1,
        },

        tag: {
            type: String,
            default: 'div',
        },
    },

    data() {
        return {
            containerClassGroups: ['flexDirection'],
            searchKeywords: [],
            formId: '',
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
        }),
        containerClasses() {
            return this.htmlClass.filter((className) => {
                const classDefinition = getClassDefinition(className);
                const { tailwindDefinitionProp } = classDefinition;

                return this.containerClassGroups.includes(tailwindDefinitionProp);
            });
        },

        linkClasses() {
            return this.htmlClass.filter((className) => {
                const classDefinition = getClassDefinition(className);
                const { tailwindDefinitionProp } = classDefinition;

                return !this.containerClassGroups.includes(tailwindDefinitionProp);
            });
        },

        hasKeywords() {
            return !!this.searchKeywords.length;
        },
    },

    watch: {
        hasKeywords() {
            if (!this.hasKeywords) {
                this.fireFlexEvent('NO_KEYWORDS');
            } else {
                this.fireFlexEvent('SHOW_KEYWORDS');
            }
        },
    },

    mounted() {
        this.listenOnRoot('flex::collection::query-refresh', this.getSearchKeywords);
        this.formId = this.getElementData(CUSTOM_ATTR_NAME);
        this.getSearchKeywords();
    },

    methods: {
        ...mapMutations({
            fireFlexEvent: FIRE_FLEX_EVENT,
        }),

        onKeywordRemove(keyword, i) {
            if (this.previewMode) {
                this.searchKeywords.splice(i, 1);
                this.clearForm(keyword);
                this.updateQueryString(keyword);
                this.emitOnRoot('flex::collection::query-refresh');
            }
        },

        updateQueryString(keyword) {
            const urlParams = new URLSearchParams(window.location.search);
            const qsParam = urlParams.get(keyword.name);

            if (qsParam) {
                if (qsParam.indexOf('|') > -1) {
                    const values = qsParam.split('|');
                    const newValues = [];
                    values.forEach((v) => {
                        if (v !== keyword.value) {
                            newValues.push(v);
                        }
                    });
                    if (newValues.length > 0) {
                        urlParams.set(keyword.name, newValues.join('|'));
                    } else {
                        urlParams.delete(keyword.name);
                    }
                } else {
                    urlParams.delete(keyword.name);
                }
            }

            window.history.replaceState({}, '', `${ window.location.pathname }?${ urlParams }`);
        },

        clearForm(keyword) {
            const formElement = document.getElementById(this.formId);

            if (formElement) {
                Object.keys(formElement.elements).forEach((i) => {
                    const elem = formElement.elements[i];

                    if (elem.name === keyword.name) {
                        if (elem.localName === 'input' && elem.value === keyword.value && elem.type !== 'button') {
                            if (elem.type === 'checkbox' || elem.type === 'radio') {
                                elem.checked = false;
                            } else if (elem.type !== 'submit') {
                                elem.value = '';
                            }
                        } else if (elem.localName === 'select' && elem.options) {
                            Object.keys(elem.selectedOptions).forEach((j) => {
                                if (elem.selectedOptions[j].value !== keyword.value) {
                                    elem.selectedOptions[j].selected = false;
                                }
                            });
                        }
                    }
                });
            }
        },

        getSearchKeywords() {
            const urlParams = new URLSearchParams(window.location.search);

            this.searchKeywords = [];

            urlParams.forEach((value, key) => {
                if (value && this.searchKeywords.filter(i => i.name === key).length === 0 && key !== 'page' && key.indexOf('@@') < 0) {
                    if (value.indexOf('|') > -1) {
                        const values = value.split('|');

                        values.forEach((val) => {
                            this.searchKeywords.push({ name: key, value: this.formatValue(val), displayName: key.replace('__', '').replace('loc_', '').replace('_', ' ').replace('query', 'keywords') });
                        });
                    } else {
                        this.searchKeywords.push({ name: key, value: this.formatValue(value), displayName: key.replace('__', '').replace('loc_', '').replace('_', ' ').replace('query', 'keywords') });
                    }
                }
            });
        },

       formatValue(value) {
            if (value.match(/\[.*TO.*\]/)) {
                let match = value.match(/\d{4}-\d-\d{2}/);
                if (match && match.length > 0) {
                    return formatDate(match[0], 'MM/dd/yyyy', true);
                }
                match = value.match(/\d{4}-\d{2}-\d{2}/);
                if (match && match.length > 0) {
                    return formatDate(match[0], 'MM/dd/yyyy', true);
                }
            }

            return value;
        },
    },
};
</script>

<style lang="scss" scoped>
.fc-keywords {
    &__list {
        display: flex;
        flex-wrap: wrap;
    }

    &__item {
        margin-top: 1em;
        margin-right: 1em;
    }

    &__icon {
        font-size: 1.125em;
        margin-left: 0.75em;
    }
}
</style>
